/**
 * Site-wide configuration — persisted in Firestore under
 * `settings/default` (site settings) and `theme/default` (theme).
 *
 * This module is intentionally client-safe (no server-only imports). The
 * Firestore-backed store lives in `lib/site-settings-store.ts`.
 */

export interface SiteSettings {
  // General
  siteName: string
  siteDescription: string
  siteUrl: string
  contactEmail: string
  // Social
  facebookUrl: string
  twitterUrl: string
  linkedinUrl: string
  instagramUrl: string
  // Email
  smtpHost: string
  smtpPort: string
  smtpUser: string
  smtpPassword: string
  fromEmail: string
  fromName: string
  // Security
  enableRegistration: boolean
  requireEmailVerification: boolean
  maxLoginAttempts: number
  sessionTimeout: number
  // SEO
  googleAnalyticsId: string
  googleTagManagerId: string
  metaTitle: string
  metaDescription: string
}

export interface ThemeSettings {
  // Colors
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  textColor: string
  mutedColor: string
  // Typography
  fontFamily: string
  headingFont: string
  fontSize: string
  lineHeight: string
  // Layout
  containerWidth: string
  borderRadius: string
  spacing: string
  // Features
  darkMode: boolean
  animations: boolean
  shadows: boolean
  gradients: boolean
  // Header
  headerStyle: string
  headerHeight: string
  stickyHeader: boolean
  // Footer
  footerStyle: string
  showSocialLinks: boolean
  showNewsletter: boolean
}

/** Brand-correct defaults (navy `#002d5c`, gold `#c4a056`, paper `#f3efe6`). */
export const DEFAULT_THEME: ThemeSettings = {
  primaryColor: "#c4a056",
  secondaryColor: "#002d5c",
  accentColor: "#c4a056",
  backgroundColor: "#f3efe6",
  textColor: "#102338",
  mutedColor: "#5c6a7d",
  fontFamily: "Inter",
  headingFont: "Playfair Display",
  fontSize: "16px",
  lineHeight: "1.6",
  containerWidth: "72rem",
  borderRadius: "0.5rem",
  spacing: "1rem",
  darkMode: false,
  animations: true,
  shadows: true,
  gradients: true,
  headerStyle: "default",
  headerHeight: "80px",
  stickyHeader: true,
  footerStyle: "default",
  showSocialLinks: true,
  showNewsletter: true,
}

export const DEFAULT_SETTINGS: SiteSettings = {
  siteName: "The Vantage Crest",
  siteDescription: "Connecting Businesses. Opening Markets. Building Partnerships.",
  siteUrl: "https://thevantagecrest.com",
  contactEmail: "hello@thevantagecrest.com",
  facebookUrl: "",
  twitterUrl: "",
  linkedinUrl: "",
  instagramUrl: "",
  smtpHost: process.env.SMTP_HOST || "",
  smtpPort: process.env.SMTP_PORT || "465",
  smtpUser: process.env.SMTP_USER || "",
  smtpPassword: process.env.SMTP_PASS || "",
  fromEmail: process.env.FROM_EMAIL || "hello@thevantagecrest.com",
  fromName: process.env.FROM_NAME || "The Vantage Crest",
  enableRegistration: true,
  requireEmailVerification: false,
  maxLoginAttempts: 5,
  sessionTimeout: 24,
  googleAnalyticsId: "",
  googleTagManagerId: "",
  metaTitle: "The Vantage Crest — Executive Leadership & International Partnerships",
  metaDescription:
    "Connecting Businesses. Opening Markets. Building Partnerships. An international business and partnership hub across the UAE/GCC, India, SE Asia, Europe, and North America.",
}

/**
 * Build a `:root` CSS block from theme settings — injected client-side
 * by <ThemeStyle /> so saved admin theme changes apply to the live site.
 */
export function buildThemeCss(theme: ThemeSettings): string {
  const rounded = theme.borderRadius || "0.5rem"
  return `/* Vantage Crest theme (admin-customizable) */
:root {
  --background: ${theme.backgroundColor};
  --foreground: ${theme.textColor};
  --primary: ${theme.primaryColor};
  --secondary: ${theme.secondaryColor};
  --accent: ${theme.accentColor};
  --muted-foreground: ${theme.mutedColor};
  --radius: ${rounded};
}
html { font-size: ${theme.fontSize || "16px"}; }
body { line-height: ${theme.lineHeight || "1.6"}; }
.font-heading, h1, h2, h3, h4, h5, h6 { font-family: "${theme.headingFont}", serif; }
.font-sans body, body { font-family: "${theme.fontFamily}", sans-serif; }
`
}

/** Google-fonts names we can safely hot-load via a stylesheet link. */
export const AVAILABLE_FONTS = [
  "Inter",
  "Playfair Display",
  "Roboto",
  "Open Sans",
  "Lato",
  "Poppins",
  "Montserrat",
] as const

export function fontFamilyCss(name: string): string {
  const safe = name.replace(/["']/g, "")
  return `family=${safe.split(" ").join("+")}:wght@400;500;600;700&display=swap`
}